<template>
  <div>
    <!--Not 10DLC Verified-->
    <template v-if="!is10DlcVerified">
      <callout-message>
        Your campaign is not 10DLC verified. Your texts will probably be blocked by carriers.
        <router-link :to="{ name: 'trustProfile' }">Manage 10DLC Registration</router-link>
      </callout-message>
      <br />
    </template>

    <template v-if="phoneNumberOptions.length > 0 && toPhoneOptions.length > 0">
      <form-form @submit="handleSend()" :submit-error="submitError" :validator="$v">
        <form-select empty="Choose Template..."
                     v-model="smsTemplateId"
                     :options="smsTemplateOptions">
          <form-help-block v-if="smsTemplateOptions && smsTemplateOptions.length === 0">
            &nbsp;<a @click="goToRoute({ name: 'smsTemplates' })">Create a Template</a>
          </form-help-block>

        </form-select>
        <form-select empty="To Phone Number..."
                     select-class="fs-block"
                     v-model="toPhone"
                     :options="toPhoneOptions"
                     :validator="$v.toPhone">
        </form-select>
        <form-textarea placeholder="Message..." v-model="body" :validator="$v.body" :rows="5"></form-textarea>
        <p class="text-danger" v-if="isTooLong">
          Message is too long. Must be less than 1,580 characters. Currently: {{ messageLength }}
        </p>
        <form-select empty="From Phone Number..."
                     v-model="phoneNumberId"
                     :options="phoneNumberOptions"
                     :validator="$v.phoneNumberId">
        </form-select>
        <template v-if="contact.do_not_contact">
          <div class="label alert">Do Not Contact</div>
        </template>
        <template v-else>
          <form-submit :loading="isLoading">Send Text Message</form-submit>
        </template>
      </form-form>
      <div class="margin-top-large">
        <a href="https://help.calltime.ai/hc/en-us/articles/7914717651860-Best-Practices-for-P2P-Texting" target="_blank">
          Best Practices for Texting
        </a>
      </div>
    </template>

    <!--No Phone Numbers-->
    <div v-else-if="phoneNumberOptions.length === 0">
      <p>You can use Call Time to quickly send templated follow-up text messages to any of your contacts.</p>
      <p>To get started, you must first setup a phone number in your campaign settings.</p>
      <p>
        <a class="button" @click="goToRoute({ name: 'phoneNumbers' })">Setup Phone Number</a>
      </p>
    </div>

    <!--No Contact Phones-->
    <div v-else-if="toPhoneOptions.length === 0">
      <p>You do not have a phone number saved for this contact. You can add one by editing the contact.</p>
      <p>
        <a class="button" @click="goToRoute({ name: 'editContact', params: { id: contact.id } })">Edit Contact</a>
      </p>
    </div>
  </div>
</template>

<script>
import { formsMixin } from '../../mixins/forms/formsMixin'
import { phoneNumbersQuery } from '../../graphql/phoneNumbers'
import { smsTemplatesQuery, smsTemplateQuery } from '../../graphql/smsTemplates'
import { sendSmsMutation } from '../../graphql/sms'
import { templateMergeTagsQuery } from '../../graphql/emailTemplates'
import { compileTemplateFromContact } from '../../services/TemplatesService'
import { required } from 'vuelidate/lib/validators'
import { campaignQuery } from '@/graphql/campaign'
import CalloutMessage from '../Common/CalloutMessage'

export default {
  components: {
    CalloutMessage
  },
  mixins: [
    formsMixin
  ],
  props: {
    contact: Object
  },
  data () {
    return {
      // if there is only one phone for the contact, then select it as the default address
      toPhone: this.contact && this.contact.phones && this.contact.phones.length === 1 ? this.contact.phones[0].number : null,
      phoneNumberId: null,
      smsTemplateId: null,
      body: '',
      submitError: null,
      isSending: false
    }
  },
  apollo: {
    campaign: {
      query: campaignQuery
    },
    phoneNumbers: {
      query: phoneNumbersQuery,
      variables: {
        type: 'twilio'
      },
      update (data) {
        // if there's only one phone number, set it as the sending phone number
        if (data.phoneNumbers && data.phoneNumbers.length === 1) {
          this.phoneNumberId = data.phoneNumbers[0].id
        }
        return data.phoneNumbers
      }
    },
    smsTemplates: smsTemplatesQuery,
    smsTemplate: {
      query: smsTemplateQuery,
      variables () {
        return {
          id: this.smsTemplateId
        }
      },
      skip () {
        return !this.smsTemplateId
      }
    },
    templateMergeTags: {
      query: templateMergeTagsQuery,
      variables () {
        return {
          contactId: this.contact.id
        }
      }
    }
  },
  validations: {
    toPhone: {
      required
    },
    body: {
      required
    },
    phoneNumberId: {
      required
    }
  },
  computed: {
    messageLength () {
      return this.body ? this.body.length : 0
    },
    isTooLong () {
      return this.messageLength > 1580
    },
    is10DlcVerified () {
      return !!this.campaign && !!this.campaign.is_10dlc_approved
    },
    toPhoneOptions () {
      if (!this.contact.phones) {
        return []
      }
      return this.contact.phones.map(phone => {
        return {
          text: phone.number,
          value: phone.number
        }
      })
    },
    phoneNumberOptions () {
      if (this.phoneNumbers && this.phoneNumbers.length > 0) {
        return this.phoneNumbers.map(phoneNumber => {
          return {
            text: phoneNumber.phone_number_formatted,
            value: phoneNumber.id
          }
        })
      }
      return []
    },
    smsTemplateOptions () {
      if (this.smsTemplates && this.smsTemplates.length > 0) {
        return this.smsTemplates.map(smsTemplate => {
          return {
            text: smsTemplate.name,
            value: smsTemplate.id
          }
        })
      }
      return []
    },
    mergeTagValues () {
      if (this.templateMergeTags && this.templateMergeTags.length > 0) {
        const values = {}
        this.templateMergeTags.forEach(tag => {
          values[tag.key] = tag.value
        })
        return values
      }
      return null
    },
    isLoading () {
      return this.$apollo.loading || this.isSending
    }
  },
  methods: {
    async handleSend () {
      if (this.isTooLong) {
        this.submitError = new Error('The message is too long.')
        return
      }
      this.submitError = null
      this.isSending = true
      // send the message
      try {
        await this.$apollo.mutate({
          mutation: sendSmsMutation,
          variables: {
            phoneNumberId: this.phoneNumberId,
            to: this.toPhone,
            body: this.body,
            smsTemplateId: this.smsTemplateId
          }
        })
      } catch (e) {
        this.isSending = false
        console.error(e)
        this.submitError = e
        return
      }
      // close the form and show success
      this.$notifications.feedbackNotification('Message sent', 'success')
      this.smsTemplateId = null
      this.$emit('close')
    },
    applySelectedTemplate () {
      // if there is no template, clear the form
      if (!this.smsTemplate || !this.smsTemplate.id) {
        this.body = ''
        return
      }
      // otherwise, update the template
      this.body = compileTemplateFromContact(this.smsTemplate.body, this.contact)
    },
    goToRoute (route) {
      // navigate
      this.$router.push(route)
      // emit the close event to close the actions bar
      this.$emit('close')
    }
  },
  watch: {
    'smsTemplate.id': {
      handler (newValue) {
        // whenever the template updates, apply it to the form
        this.applySelectedTemplate()
      }
    }
  }
}
</script>
